// @flow
import * as React from 'react';
import { Link } from 'react-router-dom';
import SimpleTable from '@localytics/loc-components/lib/SimpleTable';
import { formatNum } from '@localytics/loc-components/lib/Utils/number';
import {
  stringSort,
  numberSort,
  dateSort
} from '@localytics/loc-components/lib/TableUtils/sort';
import { utc } from 'moment';
import TableActionMenu from './TableActionMenu';
import { VERSION_STATE_MAP } from '../constants/ApiTypes';
import { STATUS_STYLE_MAP } from '../constants/Style';
import './stylesheets/OrchestrationTable.scss';

const ORCHS_PER_PAGE = 6;

const TABLE_COLUMNS = [
  {
    key: 'name',
    label: 'TITLE',
    sortable: true,
    searchable: true
  },
  {
    key: 'updatedAt',
    label: 'LAST UPDATED',
    sortable: true,
    type: 'date'
  },
  {
    key: 'activeUsers',
    label: 'ACTIVE USERS',
    sortable: true,
    type: 'number'
  },
  {
    key: 'totalUsers',
    label: 'TOTAL USERS',
    sortable: true,
    type: 'number'
  },
  {
    key: 'control',
    label: '',
    sortable: false
  }
];

const TABLE_FILTERS = [
  {
    key: 'name',
    label: 'Status',
    values: Object.keys(VERSION_STATE_MAP).map(key => ({
      key,
      label: VERSION_STATE_MAP[key]
    }))
  }
];

type EntryPropTypes = {
  name: string,
  state: string,
  versionNumber: number,
  appGroupName: string,
  orgId: number,
  orchId: number,
  versionId: number
};

const OrchestrationEntry = (props: EntryPropTypes) => {
  const statusIndicatorStyle = {
    border: `4px solid ${STATUS_STYLE_MAP[props.state]}`,
    borderRadius: '1px',
    margin: '0 10px 2px 0'
  };
  return (
    <Link
      to={`/show?orgId=${props.orgId}&orchId=${props.orchId}&versionId=${
        props.versionId
      }`}
      style={{ textDecoration: 'none' }}
    >
      <button className="OrchestrationTable__RowTitle">
        <span className="OrchestrationTable__RowName">{props.name}</span>
        <div className="OrchestrationTable__SubTitle">
          {props.versionNumber > 0 && (
            <div
              className="OrchestrationTable__RowStatus"
              style={statusIndicatorStyle}
            />
          )}

          <div className="OrchestrationTable__RowState">
            {props.versionNumber > 0 && (
              <div className="OrchestrationTable__RowState__Version">
                {`${VERSION_STATE_MAP[props.state]} ${props.versionNumber}`}
              </div>
            )}
            {props.appGroupName && (
              <div className="OrchestrationTable__RowState__Separator">
                &#xb7;
              </div>
            )}
            {props.appGroupName && (
              <div className="OrchestrationTable__RowState__AppGroup">
                {props.appGroupName}
              </div>
            )}
          </div>
        </div>
      </button>
    </Link>
  );
};

type TableData = {
  activeUsers: number,
  editingState: $Keys<typeof VERSION_STATE_MAP>,
  orchId: number,
  orgId: number,
  name: string,
  totalUsers: number,
  updatedAt: string,
  versionId: number,
  versionNumber: number,
  appGroupName: string,
  hasOnlyDraftVersions: boolean
};

type OrchestrationTablePropTypes = {
  identifier: string,
  tableData: Array<TableData>,
  orchestrationsLoaded: boolean,
  orchestrationsError: boolean,
  deleteOrchestration: Function,
  emptyState: React.Node,
  isSearching: Function
};

type selectedFilterType = {
  name: Array<{ label: string, key: string }>
};

type OrchestrationTableStateTypes = {
  sort: {
    column: string,
    direction: 'asc' | 'desc' | string,
    type: 'string' | 'date' | 'number' | string
  },
  selectedFilters: selectedFilterType,
  searchTerm: string,
  offset: number
};

const searchData = (filteredData, searchTerm) =>
  filteredData.filter(
    t =>
      searchTerm === ''
        ? true
        : t.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1
  );

const filterData = (sortedData, selectedFilter: selectedFilterType) => {
  const { name = [] } = selectedFilter;
  const filteredStates = name.map(n => n.key);
  return sortedData.filter(
    t =>
      name.length === 0 ? true : filteredStates.indexOf(t.editingState) > -1
  );
};

const pageData = (filteredData, offset) =>
  filteredData.slice(offset, offset + ORCHS_PER_PAGE);

const sortData = (
  tableData: Array<TableData>,
  column: string,
  direction: string,
  columnType: string
) => {
  const dataCopy = tableData.slice();

  if (columnType === 'number') {
    return dataCopy.sort(numberSort(column, direction));
  } else if (columnType === 'date') {
    return dataCopy.sort(dateSort(column, direction));
  }

  return dataCopy.sort(stringSort(column, direction));
};

export class OrchestrationTable extends React.Component<
  OrchestrationTablePropTypes,
  OrchestrationTableStateTypes
> {
  constructor(props: OrchestrationTablePropTypes) {
    super(props);
    this.state = {
      sort: { column: 'updatedAt', direction: 'desc', type: 'date' },
      selectedFilters: { name: [] },
      searchTerm: '',
      offset: 0
    };
  }

  sortTable(column: string, direction: string, type: string) {
    this.setState({
      sort: { column, direction, type }
    });
  }

  mapOrchDataToTable(filteredData: Array<TableData>): any[] {
    const orchDataTable = filteredData.map(o => ({
      name: (
        <OrchestrationEntry
          name={o.name}
          state={o.editingState}
          versionNumber={o.versionNumber}
          appGroupName={o.appGroupName}
          orgId={o.orgId}
          orchId={o.orchId}
          versionId={o.versionId}
        />
      ),
      updatedAt: utc(o.updatedAt).fromNow(),
      activeUsers: formatNum(o.activeUsers),
      totalUsers: formatNum(o.totalUsers),
      control: <TableActionMenu {...this.props} orchData={o} />
    }));
    return orchDataTable;
  }

  searchTable(searchTerm: string, callback: Function) {
    this.setState({
      searchTerm,
      offset: 0
    });
    if (searchTerm) {
      callback(searchTerm);
    }
  }

  filterTable(selectedFilter: selectedFilterType) {
    this.setState({
      selectedFilters: selectedFilter,
      offset: 0
    });
  }

  changePage(dataOffset: number) {
    this.setState({
      offset: dataOffset
    });
  }

  render() {
    const {
      identifier,
      orchestrationsLoaded,
      orchestrationsError,
      emptyState
    } = this.props;
    const { sort, selectedFilters, offset, searchTerm } = this.state;
    const { isSearching } = this.props;
    const { tableData = [] } = this.props;
    const sortedData = sortData(
      tableData,
      sort.column,
      sort.direction,
      sort.type
    );
    const filteredData = searchData(
      filterData(sortedData, selectedFilters),
      searchTerm
    );
    const pagedData = pageData(filteredData, offset);

    return (
      <div className="OrchestrationTable__Container">
        <SimpleTable
          identifier={identifier}
          loading={!orchestrationsLoaded}
          empty={filteredData.length === 0}
          emptyState={filteredData.length === 0 && emptyState}
          errored={orchestrationsError}
          columns={TABLE_COLUMNS}
          data={this.mapOrchDataToTable(pagedData)}
          className="OrchestrationTable__Table"
          onSearch={newSearchTerm =>
            this.searchTable(newSearchTerm, isSearching)
          }
          sort={sort}
          onSort={(column, direction, type) =>
            this.sortTable(column, direction, type)
          }
          filters={TABLE_FILTERS}
          selectedFilter={selectedFilters}
          onFilter={selectedFilter => this.filterTable(selectedFilter)}
          perPage={ORCHS_PER_PAGE}
          limit={ORCHS_PER_PAGE}
          onPageChange={pageIdx => this.changePage(pageIdx)}
          offset={offset}
          totalCount={filteredData.length}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/OrchestrationTable.jsx